
import Link from "next/link";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import type { InViewHookResponse } from "react-intersection-observer";

import type { VariantPage } from "@/lib/api/formatting/rorosTweed/productPage";
import getPriceList from "@/lib/centra/getPriceList";
import useDevice from "@/lib/hooks/useDevice";
import useHasHydrated from "@/lib/hooks/useHasHydrated";
import useSelection from "@/lib/hooks/useSelection";
import useFooterVisible from "@/lib/stateManagement/useElementInView";

import Downloads from "./Downloads";
import GarmentCare from "./GarmentCare";
import styles from "./productDetails.module.scss";
import ProductModal from "./ProductModal";
import Specification from "./Specification";
import StoreLocator from "./StoreLocator";
import Variants from "./Variants";

type ProductDetailsUI = NonNullable<VariantPage["info"]> & {
	addToCart: () => void;
	observer: InViewHookResponse;
};

const UI = ({
	details,
	addToCart,
	specifications,
	garmentCare,
	downloads,
	observer,
	productModal,
}: ProductDetailsUI) => {
	const { priceList } = useSelection();
	const { visible } = useFooterVisible();
	const hydrated = useHasHydrated();
	const { ref, inView } = observer;
	const { mobile } = useDevice();
	const t = useTranslations();

	if (!details) {
		return <></>;
	}

	const name = details.name;
	const id = details.id;
	const variantName = details.variantName;
	const designer = details.designer;
	const price = getPriceList(details.prices || [], +priceList);
	const description = { __html: details.description || "" };
	const variants = details.variants;
	const sku = details.sku;
	const stock = details.stock;

	return (
		<>
			<section className={styles.container}>
				<div className={styles.titleContainer} ref={ref}>
					<div className={styles.nameContainer}>
						<h1 className={styles.productName}>{name}</h1>
						<Link href={"/designer/" + (designer?.slug || "#")} className={styles.designerName}>
							{designer?.name}
						</Link>
					</div>
					<div className={styles.priceContainer}>
						<p>{price.price}</p>
					</div>
				</div>
				<div className={styles.sectionContainer}>
					<div
						className={styles.description}
						dangerouslySetInnerHTML={description}
					/>
				</div>
				<div className={styles.variantsContainer}>
					<Variants variants={variants} variantName={variantName} sku={sku} />
				</div>
				<button
					className={`${styles.addToCart} ${stock ? "" : styles.noStock}`}
					onClick={addToCart}
				>
					{stock ? t("addToCart") : t("outOfStock")}
				</button>
				<div className={styles.accordions}>
					{id && <Specification id={id} specification={specifications} />}
					{garmentCare && <GarmentCare {...garmentCare} />}

					{downloads && <Downloads {...downloads} />}
					<StoreLocator />
				</div>
			</section>
			{!mobile &&
				hydrated &&
				createPortal(
					<ProductModal data={productModal} show={!inView && !visible} />,
					document.body,
				)}
		</>
	);
};

export default UI;
